import React from 'react';
import tw from 'twin.macro';
import { Cart } from 'containers/Billing/types';
import { Plan } from 'types/plan';
import EURCurrency from 'components/EURCurrency';
import { useTranslation } from 'react-i18next';
import { useCustomerActivePromotionCodes } from 'queries/coupons/useGetCustomerPromotionCodes';
import { PromotionCode } from 'types/user';
import ProgressBar from 'components/ProgressBar';
import { Separator } from 'containers/Livrets/LivretEval/LivretEleve/Separator';
import { PlanTag } from 'containers/Pricing/PlanTag';

interface Props {
  cart: Cart;
  plans: Plan[];
  showMissingAboToReachNextPromotion?: boolean;
}

function computeDiscount(
  cart: Cart,
  essentialYearlyPrice: number,
  integralYearlyPrice: number,
  promotionCode?: PromotionCode,
) {
  const totalQuantity = cart.essentielQuantity + cart.integralQuantity;

  const totalPrice =
    cart.essentielQuantity * essentialYearlyPrice +
    cart.integralQuantity * integralYearlyPrice;

  if (promotionCode?.percentOff) {
    return {
      discountAmount: (totalPrice * promotionCode.percentOff) / 100,
      discountPercent: promotionCode.percentOff,
    };
  }
  if (promotionCode?.amountOff) {
    return {
      discountAmount: promotionCode.amountOff,
      discountPercent: 100 * (promotionCode.amountOff / totalPrice),
    };
  }
  if (totalQuantity >= 8) {
    return {
      discountAmount: totalPrice * 0.2,
      discountPercent: 20,
    };
  }
  if (totalQuantity >= 4) {
    return {
      discountAmount: totalPrice * 0.1,
      discountPercent: 10,
    };
  }

  return {
    discountAmount: 0,
    discountPercent: 0,
  };
}

function Row({ children }: { children: any }) {
  return (
    <div tw="flex justify-between gap-x-2">
      <div tw="flex items-baseline gap-x-2 flex-wrap">{children[0]}</div>
      <div>{children[1]}</div>
    </div>
  );
}

export function Totaux(props: Props) {
  const { cart, showMissingAboToReachNextPromotion = true } = props;
  const { t } = useTranslation(['userSettings']);
  const integralPlan = props.plans.find(plan => plan.id === 'integral');
  const essentielPlan = props.plans.find(plan => plan.id === 'essentiel');
  const priceEssentielYearly = essentielPlan?.priceYearly || 0;
  const priceIntegralYearly = integralPlan?.priceYearly || 0;
  const { data: promotionCodes = [] } = useCustomerActivePromotionCodes();

  const pendingPromotionCodes = promotionCodes.filter(
    p => p.maxRedemptions === 0 || p.timesRedeemed < p.maxRedemptions,
  );
  const nextPromotionCode: PromotionCode | undefined = pendingPromotionCodes[0];

  const { discountAmount, discountPercent } = computeDiscount(
    cart,
    priceEssentielYearly,
    priceIntegralYearly,
    nextPromotionCode,
  );

  const missingAboToReach10Percent =
    4 - (cart.integralQuantity + cart.essentielQuantity);
  const missingAboToReach20Percent =
    8 - (cart.integralQuantity + cart.essentielQuantity);
  return (
    <div css={[showMissingAboToReachNextPromotion && tw`my-6`]}>
      {showMissingAboToReachNextPromotion && (
        <>
          {!nextPromotionCode && missingAboToReach10Percent > 0 && (
            <div tw="mb-6">
              <div tw="mb-1 text-sm text-neutral-quiet">
                {t('userSettings:schoolSubscription.missing10%', {
                  amount: missingAboToReach10Percent,
                })}
              </div>
              <ProgressBar value={1 - 0.1 * missingAboToReach10Percent} />
            </div>
          )}
          {!nextPromotionCode &&
            missingAboToReach10Percent <= 0 &&
            missingAboToReach20Percent > 0 && (
              <div tw="mb-6">
                <div tw="mb-1 text-sm text-neutral-quiet">
                  {t('userSettings:schoolSubscription.missing20%', {
                    amount: missingAboToReach20Percent,
                  })}
                </div>
                <ProgressBar value={1 - 0.1 * missingAboToReach20Percent} />
              </div>
            )}
        </>
      )}
      <div tw="space-y-0">
        <div tw="space-y-2">
          {cart.essentielQuantity > 0 && essentielPlan?.priceYearly && (
            <Row>
              <>
                <p>{t('userSettings:licences.abo1y')}</p>
                <PlanTag plan={essentielPlan} />
                <div>x{cart.essentielQuantity}</div>
              </>
              <div>
                <EURCurrency
                  tw="font-semibold"
                  fixedDecimalScale
                  amount={cart.essentielQuantity * priceEssentielYearly}
                />
              </div>
            </Row>
          )}
          {cart.integralQuantity > 0 && integralPlan && priceIntegralYearly && (
            <Row>
              <>
                <p>{t('userSettings:licences.abo1y')}</p>
                <div>
                  <PlanTag plan={integralPlan} />
                </div>
                <div>x{cart.integralQuantity}</div>
              </>

              <EURCurrency
                fixedDecimalScale
                tw="font-semibold text-base"
                amount={cart.integralQuantity * priceIntegralYearly}
              />
            </Row>
          )}

          {discountAmount > 0 && (
            <Row>
              <div>
                {t('userSettings:schoolSubscription.discount', {
                  discountPercent,
                })}
              </div>
              <EURCurrency
                fixedDecimalScale
                tw="font-semibold text-base text-positive-moderate"
                amount={discountAmount * -1}
              />
            </Row>
          )}
        </div>
        <div tw="px-6">
          <Separator tw="my-4" />
        </div>
        <Row>
          {t('userSettings:schoolSubscription.total')}
          <EURCurrency
            tw="font-extrabold text-lg"
            fixedDecimalScale
            amount={
              cart.essentielQuantity * priceEssentielYearly +
              cart.integralQuantity * priceIntegralYearly -
              discountAmount
            }
          />
        </Row>
      </div>
    </div>
  );
}
